import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { AuthService, TokenService } from '../../app/services';
import { AUTH_PROVIDER_TYPES } from '../../app/shared/constants';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class TokenInterceptorService implements HttpInterceptor {
    private readonly TOKEN_RENEWAL_THRESHOLD_SECONDS = 60;
    private _renewalInProgress$: Observable<string | null> | null = null;

    constructor(
        private _tokenService: TokenService,
        private _authService: AuthService
    ) { }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.shouldAttachToken(req)) {
            return next.handle(req);
        }

        return this.prepareAuthorizedRequest(req).pipe(
            switchMap((authorizedRequest: HttpRequest<unknown>) => next.handle(authorizedRequest))
        );
    }

    private shouldAttachToken(req: HttpRequest<unknown>): boolean {
        return req.url.includes(environment.api) && !req.url.includes('/authenticate');
    }

    private prepareAuthorizedRequest(req: HttpRequest<unknown>): Observable<HttpRequest<unknown>> {
        if (this._tokenService.tokenIsStillValid(0) && this._tokenService.currentToken) {
            if (!this._tokenService.tokenIsStillValid(this.TOKEN_RENEWAL_THRESHOLD_SECONDS)) {
                this.refreshTokenIfExpiringSoon();
            }

            return of(this.attachAuthorizationHeader(req, this._tokenService.currentToken));
        }

        return this.renewTokenIfNeeded().pipe(
            map((token: string | null) => token ? this.attachAuthorizationHeader(req, token) : req)
        );
    }

    private attachAuthorizationHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
        return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    private refreshTokenIfExpiringSoon(): void {
        if (this._tokenService.tokenIsStillValid(this.TOKEN_RENEWAL_THRESHOLD_SECONDS)) {
            return;
        }

        if (this._renewalInProgress$) {
            return;
        }

        this.renewTokenIfNeeded().pipe(take(1)).subscribe({
            error: (err) => {
                console.warn('Token renewal failed:', err);
            }
        });
    }

    private renewTokenIfNeeded(): Observable<string | null> {
        if (this._renewalInProgress$) {
            return this._renewalInProgress$;
        }

        const tokenType = this._tokenService.getTokenType();

        if (tokenType !== AUTH_PROVIDER_TYPES.ENTRA_ID && tokenType !== AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID) {
            return of(null);
        }

        const tokenEmail = this._tokenService.getTokenEmail();

        if (!tokenEmail) {
            return of(null);
        }

        this._renewalInProgress$ = this._authService.renewToken(tokenEmail).pipe(
            tap((token: string | null) => {
                if (token) {
                    this._tokenService.setToken(token);
                }
            }),
            shareReplay(1),
            finalize(() => {
                this._renewalInProgress$ = null;
            })
        );

        return this._renewalInProgress$;
    }
}
